// Stage 1a — AE Details review page (FR-1.11–1.17 / US-1a.1–1a.8). Opened from a preprint card's
// "Details" action. Renders the pitch deck in two clearly-labelled halves: the student-authored
// CLAIM half (AE sanity-checks) and the reviewer-profile half the AE is responsible for verifying.
// Partial edits save without clobbering unedited fields; refine + status changes update live (SSE).
import { useState } from "react";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AE_SUBDISCIPLINES_KEY,
  aeSubdisciplines,
  deriveJustification,
  DOMAINS,
  KEY_REVIEWER_QUESTION_KEY,
  MODEL_REVIEWERS_STRUCTURED_KEY,
  type Domain,
  type ModelReviewerFields,
  type SelectedStatus,
  type SelectedView,
} from "@rrid/shared-types";
import { api, ApiError } from "../../lib/api.js";
import { qk } from "../../lib/queryKeys.js";
import { useAuth } from "../../lib/auth.js";
import { useCan } from "../../components/Can.js";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Field,
  Input,
  Select,
  Spinner,
  StarRating,
  Textarea,
  statusTone,
} from "../../components/ui.js";
import { confirmAdvance } from "./advance-confirms.js";
import { useProductEvent } from "../../lib/telemetry.js";
import {
  CLAIM_PAIRS,
  CONTEXT_TEXT,
  PRESENTED_PROFILE_FIELDS,
  PRESENTED_TAIL,
  REVIEWER_PROFILE_FIELDS,
} from "./pitch-regions.js";
import { useFeatures } from "../../hooks/useFeatures.js";
import { RichText } from "../../components/RichText.js";
import { SimilarArticlesBoxes } from "./SimilarArticlesBoxes.js";

// Deck field descriptors — the `key` is the verbatim `Selected.pitch` key (the field contract).
// "similar" renders the three-box Similar Articles editor (Angel, Stef mtg 8/20).
type FieldKind = "textarea" | "text" | "stars" | "similar";
interface DeckField {
  key: string;
  kind: FieldKind;
  /** Display label when the raw pitch key reads poorly (defaults to the key). */
  label?: string;
  /** Hard word cap enforced while typing, with a live counter (Angel, Stef mtg 8/20). */
  maxWords?: number;
}

const countWords = (s: string): number => (s.trim() ? s.trim().split(/\s+/).length : 0);
/** Keep the leading `max` words (plus their whitespace) — blocks typing past the cap. */
const clampWords = (s: string, max: number): string => {
  const m = s.match(new RegExp(`^(?:\\s*\\S+){0,${max}}`));
  return m ? m[0] : s;
};

// R-011: the SoftR four pitch criteria — stars + a short note each, persisted under
// `selected.pitch` with these keys (stars as "1".."5" strings like the claim ratings; the note
// under `<key> Note`). Key names are the stable field contract — document any change in the WO.
const PITCH_CRITERIA = [
  { key: "Priority(novelty)", label: "Novelty" },
  { key: "Priority(importance)", label: "Importance" },
  { key: "Priority(urgency)", label: "Urgency" },
  { key: "Priority(utility)", label: "Utility of review" },
] as const;
const criteriaNoteKey = (key: string): string => `${key} Note`;
// Angel (Stef mtg 8/20): the Importance / Urgency / Utility short notes cap at 100 words.
const CRITERIA_FIELDS: DeckField[] = PITCH_CRITERIA.flatMap((c) => [
  { key: c.key, kind: "stars" as const, label: `${c.label} (stars)` },
  {
    key: criteriaNoteKey(c.key),
    kind: "textarea" as const,
    label: `${c.label} — short note`,
    ...(c.key === "Priority(novelty)" ? {} : { maxWords: 100 }),
  },
]);
const CLAIM_FIELDS: DeckField[] = [
  { key: "Research Q1", kind: "textarea" },
  { key: "Main Claim 1", kind: "textarea" },
  { key: "Claim 1 Rating", kind: "stars" },
  { key: "Claim 1 Rating Confidence", kind: "stars" },
  { key: "Research Q2", kind: "textarea" },
  { key: "Main Claim 2", kind: "textarea" },
  { key: "Claim 2 Rating", kind: "stars" },
  { key: "Claim 2 Rating Confidence", kind: "stars" },
  { key: "Research Q3", kind: "textarea" },
  { key: "Main Claim 3", kind: "textarea" },
  { key: "Claim 3 Rating", kind: "stars" },
  { key: "Claim 3 Rating Confidence", kind: "stars" },
  // Angel (Stef mtg 8/20): presented as "Overall Strength" (pitch key unchanged).
  { key: "Strength of Evidence", kind: "stars", label: "Overall Strength" },
  { key: "Potential Limitation", kind: "textarea", label: "Potential Limitations" },
  { key: "Method Used 1", kind: "text", label: "Methods used" },
  { key: "Method Used 2", kind: "text", label: "Methods used (2)" },
  { key: "Method Used 3", kind: "text", label: "Methods used (3)" },
  // Angel (Aug 2026): not-presented, with Method 3 — SoftR parity (student-facing).
  {
    key: KEY_REVIEWER_QUESTION_KEY,
    kind: "textarea",
    label:
      "What is the most important specific question that the reviewer needs to answer about this preprint?",
  },
  { key: "Nearest Neighbor Papers", kind: "similar", label: "Similar Articles" },
  // Reason(value-add / impact / urgency) textareas removed — superseded by the four
  // Priority(*) star ratings + short notes (R-011).
  // R-011: the four SoftR criteria are filled here and presented on the pitch view.
  ...CRITERIA_FIELDS,
];
// WO-7/21 Track E: the reviewer-profile wording Angel dictated [11:34–12:25] — used verbatim in
// edit mode. Key reviewer question + similar articles live with methods (claim half / context).
const PROFILE_TEXT_FIELDS: DeckField[] = [
  { key: REVIEWER_PROFILE_FIELDS[0].key, kind: "text", label: REVIEWER_PROFILE_FIELDS[0].label },
  { key: REVIEWER_PROFILE_FIELDS[1].key, kind: "textarea", label: REVIEWER_PROFILE_FIELDS[1].label },
  { key: REVIEWER_PROFILE_FIELDS[2].key, kind: "text", label: REVIEWER_PROFILE_FIELDS[2].label },
];

// Model Peer Reviewers + suggested expertise phrases: AE fills these from To Pitch(Editorial).
const MPR_STAGES: SelectedStatus[] = ["To Pitch(Editorial)", "Selected"];
// AE presents column in presentation view: editorial meeting onward only.
const EDITORIAL_STAGES: SelectedStatus[] = ["To Pitch(Editorial)", "Selected"];

const REFINE_TONE: Record<string, "slate" | "blue" | "green" | "red"> = {
  None: "slate",
  Created: "blue",
  Refined: "green",
};
const refineTone = (s: string | null): "slate" | "blue" | "green" | "red" =>
  s == null ? "slate" : (REFINE_TONE[s] ?? (s.startsWith("Failed") ? "red" : "slate"));

function listPathFrom(state: unknown): string {
  if (state && typeof state === "object" && "from" in state && typeof (state as { from: unknown }).from === "string") {
    return `/preprints${(state as { from: string }).from}`;
  }
  return "/preprints";
}

export function PreprintDetails(): JSX.Element {
  const { id = "" } = useParams();
  useProductEvent("ui.preprint_opened", { id, path: `/preprints/${id}` });
  const { data: s, isLoading, isError } = useQuery({
    queryKey: qk.selectedOne(id),
    queryFn: () => api.get<SelectedView>(`/preprints/selected/${id}`),
    enabled: !!id,
  });

  if (isLoading) return <Spinner label="Loading preprint…" />;
  if (isError || !s) return <EmptyState>Preprint not found. <Link className="text-brand hover:underline" to="/preprints">Back to list</Link></EmptyState>;
  return <DetailsView s={s} />;
}

function DetailsView({ s }: { s: SelectedView }): JSX.Element {
  const qc = useQueryClient();
  const can = useCan();
  const navigate = useNavigate();
  const location = useLocation();
  const listPath = listPathFrom(location.state);
  const { user } = useAuth();
  const { features } = useFeatures();
  const own = !!user && !!s.assigneeEmail && s.assigneeEmail.toLowerCase() === user.email.toLowerCase();
  const ctx = { domain: s.domain };
  // July-8 FR-N1: an AE edits BOTH halves of ANY preprint regardless of domain. `advanceToPitch
  // Editorial` with no domain scope is exactly the AE-and-above test (students and QC-in-training
  // are denied it outright), so it doubles as the "am I an AE?" tell — see FR-L8's cross-domain
  // advance. The server re-checks this on every write (deckScope in routes/preprints.ts).
  const isAe = can("advanceToPitchEditorial", {});
  const canEdit = own || isAe || can("editPitchDeck", ctx);
  const canProfile = isAe || can("editSimilarArticles", ctx); // profile half + refine

  const mprStage = MPR_STAGES.includes(s.status);
  const editorialStage = EDITORIAL_STAGES.includes(s.status);

  // Presentation mode (FR-J1): a pitched deck opens read-only (whole-screen, screen-share
  // friendly, split-screen per FR-J7); editing is a separate mode entered deliberately.
  const pitched = s.status !== "Unread" && s.status !== "Taken";
  const [editMode, setEditMode] = useState(!pitched);

  // Draft = only the fields the AE has touched, so Save sends a minimal partial merge (FR-1.16).
  const [draft, setDraft] = useState<Record<string, string>>({});
  const dirtyKeys = Object.keys(draft);
  const dirty = dirtyKeys.length > 0;
  const valueOf = (key: string): string =>
    key in draft ? draft[key]! : String((s.pitch as Record<string, unknown>)[key] ?? "");
  const setField = (key: string, value: string): void => setDraft((d) => ({ ...d, [key]: value }));

  const save = useMutation({
    mutationFn: () => {
      const pitch: Record<string, string> = {};
      for (const k of dirtyKeys) pitch[k] = draft[k] ?? "";
      return api.patch<SelectedView>(`/preprints/selected/${s.id}`, { pitch });
    },
    onSuccess: (updated) => {
      qc.setQueryData(qk.selectedOne(s.id), updated);
      qc.invalidateQueries({ queryKey: ["selected"] });
      setDraft({});
    },
  });
  const canCorrectUnclaimedDomain =
    s.status === "Unread" && can("correctDomain", { domain: s.domain, beforePitching: false });
  const correctDomain = useMutation({
    mutationFn: (domain: Domain) => api.patch<SelectedView>(`/preprints/selected/${s.id}`, { domain }),
    onSuccess: (updated) => {
      qc.setQueryData(qk.selectedOne(s.id), updated);
      qc.invalidateQueries({ queryKey: ["selected"] });
    },
  });

  const isUrgent = valueOf("Urgent") === "Urgent";
  const setUrgent = useMutation({
    mutationFn: (urgent: boolean) => api.post(`/preprints/selected/${s.id}/urgent`, { urgent }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["selected"] }),
  });

  // Angel (Stef mtg 8/20): Advance also saves — flush any dirty deck edits before the
  // status change so "Advance" is save+advance in one step (Save stays as its own button).
  const saveBeforeAdvance = async (): Promise<void> => {
    if (dirty) await save.mutateAsync();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm">
        {/* Angel (Stef mtg 8/20): back returns to the PREVIOUS view (filters intact), not the
            Unclaimed landing page. New-tab opens (no history) still fall back to the list. */}
        <button
          type="button"
          className="text-brand hover:underline"
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate(listPath))}
        >
          ← Back
        </button>
        {/* WO B3 entry point: the article-details reviewer/COI surface (FEATURE_COI-gated). */}
        {features.coi.enabled && s.preprintId && (
          <Link className="ml-auto text-brand hover:underline" to={`/preprints/${s.preprintId}/reviewers`}>
            Reviewers &amp; COI →
          </Link>
        )}
      </div>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={statusTone(s.status)}>{s.status}</Badge>
            {canCorrectUnclaimedDomain ? (
              <Select
                aria-label="Change scraping-assigned domain"
                value={s.domain ?? ""}
                disabled={correctDomain.isPending}
                onChange={(e) => {
                  const next = e.target.value as Domain;
                  if (next && next !== s.domain) correctDomain.mutate(next);
                }}
              >
                {!s.domain && <option value="">—</option>}
                {DOMAINS.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </Select>
            ) : (
              s.domain && <Badge tone="blue">{s.domain}</Badge>
            )}
            {s.gatesFunded && <Badge tone="amber">Gates</Badge>}
            {s.altmetricScore != null && (
              <Badge tone="violet">Altmetric {Number.isInteger(s.altmetricScore) ? s.altmetricScore : s.altmetricScore.toFixed(1)}</Badge>
            )}
            {isUrgent && <Badge tone="red">Urgent</Badge>}
            <span className="text-xs text-slate-500">
              Refinement: <Badge tone={refineTone(s.proposalStatus)}>{s.proposalStatus ?? "None"}</Badge>
            </span>
          </div>
          <h1 className="mt-1 text-xl font-bold">{s.title}</h1>
          <p className="text-xs text-slate-500">
            {s.assigneeEmail ? `Assigned: ${s.assigneeEmail}` : "Unassigned"}
            {s.doi && (
              <>
                {" · "}
                <a className="text-brand hover:underline" href={s.doi} target="_blank" rel="noreferrer">Open DOI</a>
              </>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {(canEdit || canProfile) && (
            <Button variant="secondary" onClick={() => setEditMode((m) => !m)} aria-pressed={editMode}>
              {editMode ? "Presentation view" : "Edit deck"}
            </Button>
          )}
          {editMode && canEdit && (
            <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
              {save.isPending ? "Saving…" : dirty ? "Save changes" : "Saved"}
            </Button>
          )}
        </div>
      </header>

      {/* FR-P11: Presentation mode is a split panel (Presented vs muted Context) — no bracket-based
          hiding. Edit mode keeps Claims / Reviewer Profile halves so every field is fillable. */}
      {!editMode ? (
        <PresentationSplit
          s={s}
          changeStatus={
            <ChangeStatus
              s={s}
              own={own}
              canProfile={canProfile}
              beforeAdvance={saveBeforeAdvance}
              onAdvancedToEditorial={() => setEditMode(true)}
              listPath={listPath}
            />
          }
        />
      ) : (
      /* Split-screen (FR-J7): Claims left, Reviewer Profile right. */
      <div className="grid gap-4 lg:grid-cols-2">
        {/* ── Claims ─────────────────────────────────────────────────── */}
        <Card>
          <CardHeader className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold">Claims</h2>
              <p className="text-xs text-slate-500">Summary of the science — AE sanity-checks.</p>
            </div>
            {editMode && (
              <Button
                variant="ghost"
                onClick={() => setUrgent.mutate(!isUrgent)}
                disabled={!canEdit || setUrgent.isPending}
                aria-pressed={isUrgent}
              >
                {isUrgent ? "Unset urgent" : "Set urgent"}
              </Button>
            )}
          </CardHeader>
          <CardBody className="space-y-3">
            {CLAIM_FIELDS.map((f) => (
              <DeckFieldInput
                key={f.key}
                field={f}
                value={valueOf(f.key)}
                onChange={(v) => setField(f.key, v)}
                readOnly={!canEdit}
                present={!editMode}
              />
            ))}
          </CardBody>
        </Card>

        {/* ── Reviewer Profile ───────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <h2 className="text-sm font-semibold">Reviewer Profile</h2>
            <p className="text-xs text-slate-500">
              Drives the Stage 2 reviewer search — drafted on claim, verified and finalized by the AE.
            </p>
          </CardHeader>
          <CardBody className="space-y-4">
            {PROFILE_TEXT_FIELDS.map((f) => (
              <DeckFieldInput
                key={f.key}
                field={f}
                value={valueOf(f.key)}
                onChange={(v) => setField(f.key, v)}
                readOnly={!canProfile && !canEdit}
                present={!editMode}
              />
            ))}

            {/* Suggested phrases: AE fills after → To Pitch(Editorial). */}
            {!editorialStage ? (
              <p className="rounded-md border border-slate-100 bg-slate-50 px-2 py-1.5 text-xs text-slate-500">
                Expertise phrases for the invite email appear once this preprint reaches To
                Pitch(Editorial) — your AE fills them then.
              </p>
            ) : (
              <AeSubdisciplineWordBox
                value={valueOf(AE_SUBDISCIPLINES_KEY)}
                onChange={(v) => setField(AE_SUBDISCIPLINES_KEY, v)}
                readOnly={!canProfile}
              />
            )}

            {/* Model Peer Reviewers: AE-owned, editorial-stage-gated (same stage as phrases). */}
            {!mprStage ? (
              <p className="rounded-md border border-slate-100 bg-slate-50 px-2 py-1.5 text-xs text-slate-500">
                Model Peer Reviewers (AE exemplars on the pitch, auto-proposed at Selected) come later
                — your AE fills them from their PROFI refinement once this preprint reaches To
                Pitch(Editorial).
              </p>
            ) : canProfile ? (
              <ModelReviewerStructuredEditor s={s} readOnly={false} />
            ) : (
              <p className="rounded-md border border-slate-100 bg-slate-50 px-2 py-1.5 text-xs text-slate-500">
                Model Peer Reviewers are AE exemplars on the pitch (auto-proposed at Selected) —
                after Refine Concepts your AE picks two Prophy-suggested reviewers. Propose a real
                person from Find Reviewers, not here. Your part of the deck ends here.
              </p>
            )}
          </CardBody>
        </Card>
      </div>
      )}

      {/* Angel (Stef mtg 8/20): Save also lives at the BOTTOM of the deck — no scroll-back-up. */}
      {editMode && canEdit && (
        <div className="flex justify-end">
          <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
            {save.isPending ? "Saving…" : dirty ? "Save changes" : "Saved"}
          </Button>
        </div>
      )}

      {/* ── Refine + Finalize gate, then the status controls (FR-N2/N3) ─
          Presentation mode renders this inside the AE column instead (Jul-31 design). */}
      {editMode && (
        <ChangeStatus
          s={s}
          own={own}
          canProfile={canProfile}
          beforeAdvance={saveBeforeAdvance}
          onAdvancedToEditorial={() => setEditMode(true)}
          listPath={listPath}
        />
      )}

      {/* Aug 19 sitting (#41 resolution): the embedded Propose a Reviewer form is gone from
          preprint Details — proposing belongs only to the Reviewer Proposals portal, so AEs
          stop dumping Model Peer Reviewers into the COI propose form (S3 Grace). */}
    </div>
  );
}

function DeckFieldInput({
  field,
  value,
  onChange,
  readOnly,
  present = false,
}: {
  field: DeckField;
  value: string;
  onChange: (v: string) => void;
  readOnly: boolean;
  /** Presentation mode (FR-J1): clean read-only text, no edit chrome. */
  present?: boolean;
}): JSX.Element {
  const label = field.label ?? field.key;
  if (present) {
    return (
      <div>
        <span className="text-xs font-medium text-slate-600">{label}</span>
        {field.kind === "stars" ? (
          <StarRating value={Number.parseInt(value, 10) || 0} readOnly label={label} />
        ) : (
          <RichText>{value}</RichText>
        )}
      </div>
    );
  }
  return (
    <Field label={label}>
      {field.kind === "similar" ? (
        <SimilarArticlesBoxes value={value} onChange={onChange} readOnly={readOnly} />
      ) : field.kind === "stars" ? (
        <StarRating value={Number.parseInt(value, 10) || 0} onChange={(n) => onChange(String(n))} readOnly={readOnly} label={label} />
      ) : field.kind === "textarea" ? (
        <>
          <Textarea
            value={value}
            onChange={(e) =>
              onChange(field.maxWords ? clampWords(e.target.value, field.maxWords) : e.target.value)
            }
            readOnly={readOnly}
          />
          {field.maxWords ? (
            <p
              className={`mt-0.5 text-right text-[11px] ${
                countWords(value) >= field.maxWords ? "font-medium text-amber-700" : "text-slate-400"
              }`}
            >
              {countWords(value)}/{field.maxWords} words
            </p>
          ) : null}
        </>
      ) : (
        <Input value={value} onChange={(e) => onChange(e.target.value)} readOnly={readOnly} />
      )}
    </Field>
  );
}

function ModelReviewers({
  reviewers,
  /** Context-rail cards match the Jul-31 mockup; suppress the shared heading there. */
  hideHeading = false,
}: {
  reviewers: SelectedView["modelReviewers"];
  hideHeading?: boolean;
}): JSX.Element {
  if (reviewers.length === 0) {
    return (
      <div>
        {!hideHeading && <h3 className="text-xs font-semibold text-slate-600">Model Peer Reviewers</h3>}
        <p className="text-xs text-slate-400">None provided.</p>
      </div>
    );
  }

  // Presentation / More Context: one card per slot matching the design mockup.
  if (hideHeading) {
    return (
      <div className="space-y-5">
        {reviewers.map((r) => {
          const justification =
            r.subdiscipline?.trim() || r.bestMatchPaperTitle?.trim()
              ? deriveJustification(r.subdiscipline?.trim() || "…", r.bestMatchPaperTitle?.trim() || "…")
              : "";
          return (
            <div key={r.slot} className="space-y-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Example of “model” peer reviewer #{r.slot}{" "}
                <span className="text-[10px] font-medium uppercase text-slate-400">not presented</span>
              </span>
              <p className="text-sm text-slate-700">
                <span className="font-medium">{r.name || `Name`}</span>
                <span className="text-slate-500"> — {r.affiliation?.trim() || "institution"}</span>
              </p>
              <p className="text-sm">
                {r.profileUrl ? (
                  <a className="text-brand hover:underline" href={r.profileUrl} target="_blank" rel="noreferrer">
                    Profile
                  </a>
                ) : (
                  <span className="text-slate-400">Profile</span>
                )}
                <span className="text-slate-400"> · </span>
                {r.pubmedUrl ? (
                  <a className="text-brand hover:underline" href={r.pubmedUrl} target="_blank" rel="noreferrer">
                    Publications
                  </a>
                ) : (
                  <span className="text-slate-400">Publications</span>
                )}
              </p>
              <div className="space-y-0.5">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Best match paper
                </span>
                {r.bestMatchPaperTitle?.trim() ? (
                  r.bestMatchUrl ? (
                    <a
                      className="block text-sm text-brand hover:underline"
                      href={r.bestMatchUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {r.bestMatchPaperTitle}
                    </a>
                  ) : (
                    <p className="text-sm text-slate-700">{r.bestMatchPaperTitle}</p>
                  )
                ) : (
                  <p className="text-sm italic text-slate-400">Paper title</p>
                )}
              </div>
              <div className="space-y-0.5">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Justification sentence
                </span>
                {justification ? (
                  <p className="text-sm italic text-slate-600">{justification}</p>
                ) : (
                  <p className="text-sm italic text-slate-400">
                    One sentence on why this reviewer is a strong match for this preprint.
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-xs font-semibold text-slate-600">Model Peer Reviewers</h3>
      <ul className="mt-1 space-y-2">
        {reviewers.map((r) => (
          <li key={r.slot} className="rounded-md border border-slate-200 p-2">
            <div className="flex items-center gap-2">
              <Badge tone="slate">#{r.slot}</Badge>
              <span className="text-sm font-medium">{r.name || `Model #${r.slot}`}</span>
              {r.profileUrl && (
                <a className="text-xs text-brand hover:underline" href={r.profileUrl} target="_blank" rel="noreferrer">
                  Profile ↗
                </a>
              )}
              {r.pubmedUrl && (
                <a className="text-xs text-brand hover:underline" href={r.pubmedUrl} target="_blank" rel="noreferrer">
                  PubMed ↗
                </a>
              )}
            </div>
            {(r.email || r.affiliation || r.subdiscipline || r.bestMatchPaperTitle) && (
              <div className="mt-1 text-xs text-slate-500">
                {[
                  r.email,
                  r.affiliation,
                  r.subdiscipline && `sub-discipline: ${r.subdiscipline}`,
                  r.bestMatchPaperTitle && `best-match: “${r.bestMatchPaperTitle}”`,
                ]
                  .filter(Boolean)
                  .join(" · ")}
                {r.bestMatchUrl && (
                  <>
                    {" · "}
                    <a className="text-brand hover:underline" href={r.bestMatchUrl}>
                      best-match link
                    </a>
                  </>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── FR-P9: structured model-reviewer editor — the full proposal-ready fields ─
// Saves the per-slot array to the pitch key so the fields survive round-trip and drive auto-propose
// at Selected. Two slots (matching the legacy `Peer Reviewer Model #1/#2`).
const MODEL_SLOTS = [1, 2];

type CooldownHit = { email: string; onCooldown: boolean; optedOut: boolean; reason: string | null };

function cooldownWarning(hit: CooldownHit): string {
  if (!hit.onCooldown) return "";
  if (hit.optedOut) return `${hit.email} has opted out of reviewing.`;
  return `${hit.email} was recently invited — pick someone else for this slot.`;
}

async function checkModelCooldown(email: string): Promise<string> {
  const trimmed = email.trim();
  if (!trimmed || !trimmed.includes("@")) return "";
  try {
    const hit = await api.get<CooldownHit>(`/preprints/selected/cooldown?email=${encodeURIComponent(trimmed)}`);
    return cooldownWarning(hit);
  } catch (err) {
    return err instanceof ApiError ? err.message : "";
  }
}

function ModelReviewerStructuredEditor({ s, readOnly }: { s: SelectedView; readOnly: boolean }): JSX.Element {
  const qc = useQueryClient();
  const seed = (): ModelReviewerFields[] =>
    MODEL_SLOTS.map((slot) => {
      const m = s.modelReviewers.find((r) => r.slot === slot);
      return {
        slot,
        name: m?.name ?? "",
        email: m?.email ?? "",
        affiliation: m?.affiliation ?? "",
        secondaryAffiliation: m?.secondaryAffiliation ?? "",
        highestDegree: m?.highestDegree ?? "",
        otherDegree: m?.otherDegree ?? "",
        title: m?.titleRole ?? "",
        otherTitles: m?.otherTitles ?? "",
        subdiscipline: m?.subdiscipline ?? "",
        bestMatchPaperTitle: m?.bestMatchPaperTitle ?? "",
        bestMatchUrl: m?.bestMatchUrl ?? "",
        profileUrl: m?.profileUrl ?? "",
        pubmedUrl: m?.pubmedUrl ?? "",
      };
    });
  const [rows, setRows] = useState<ModelReviewerFields[]>(seed);
  const [slotWarnings, setSlotWarnings] = useState<Record<number, string>>({});
  const set = (slot: number, key: keyof ModelReviewerFields, v: string): void =>
    setRows((rs) => rs.map((r) => (r.slot === slot ? { ...r, [key]: v } : r)));
  const checkSlot = async (slot: number, email: string): Promise<string> => {
    const warning = await checkModelCooldown(email);
    setSlotWarnings((cur) => ({ ...cur, [slot]: warning }));
    return warning;
  };
  const save = useMutation({
    mutationFn: async () => {
      const warnings = await Promise.all(rows.map((r) => checkSlot(r.slot, r.email ?? "")));
      if (warnings.some(Boolean)) {
        throw new ApiError(409, { error: "A model reviewer is on the recently-invited list." }, "A model reviewer is on the recently-invited list.");
      }
      return api.patch<SelectedView>(`/preprints/selected/${s.id}`, {
        // Drop entirely-empty slots so we don't persist blanks.
        pitch: {
          [MODEL_REVIEWERS_STRUCTURED_KEY]: rows.filter((r) => r.name?.trim() || r.email?.trim()),
        },
      });
    },
    onSuccess: (updated) => {
      qc.setQueryData(qk.selectedOne(s.id), updated);
      qc.invalidateQueries({ queryKey: ["selected"] });
    },
  });

  return (
    <div className="space-y-3 rounded-md border border-slate-100 p-2">
      <h3 className="text-xs font-semibold text-slate-600">
        Model Peer Reviewers — AE exemplars on the pitch (auto-proposed at Selected). Not the COI / Quality Check propose form.
      </h3>
      {/* Aug 19 sitting: the embedded Prophy suggestions panel is gone — do not rebuild
          Prophy inside the portal. Refine / model-reviewer work is one click out to Prophy
          in a new tab (FR-Q25). */}
      {!readOnly && (
        <p className="text-xs text-slate-600">
          <a
            className="font-semibold text-brand hover:underline"
            href="https://www.prophy.ai/"
            target="_blank"
            rel="noreferrer"
          >
            Open Prophy ↗
          </a>{" "}
          in a new tab to refine concepts and pick model reviewers, then enter your two picks below.
        </p>
      )}
      {rows.map((r) => (
        <div key={r.slot} className="space-y-1.5 rounded-md border border-slate-200 p-2">
          <div className="text-xs font-semibold text-slate-500">Model #{r.slot}</div>
          {/* R-013 + WO-7/21 Track G: the exact submission-portal field set — identity,
              affiliations, degrees/titles, expertise phrasing, best-match title + URL, links. */}
          <div className="grid grid-cols-2 gap-1.5">
            <Input placeholder="Name" value={r.name ?? ""} onChange={(e) => set(r.slot, "name", e.target.value)} readOnly={readOnly} />
            <Input
              placeholder="Email"
              type="email"
              value={r.email ?? ""}
              onChange={(e) => set(r.slot, "email", e.target.value)}
              onBlur={() => {
                if (!readOnly) void checkSlot(r.slot, r.email ?? "");
              }}
              readOnly={readOnly}
            />
            {slotWarnings[r.slot] && (
              <p className="col-span-2 text-xs text-red-700">{slotWarnings[r.slot]}</p>
            )}
            <Input placeholder="University / affiliation" value={r.affiliation ?? ""} onChange={(e) => set(r.slot, "affiliation", e.target.value)} readOnly={readOnly} />
            <Input placeholder="Secondary affiliation (optional)" value={r.secondaryAffiliation ?? ""} onChange={(e) => set(r.slot, "secondaryAffiliation", e.target.value)} readOnly={readOnly} />
            <Input placeholder="Highest degree *" value={r.highestDegree ?? ""} onChange={(e) => set(r.slot, "highestDegree", e.target.value)} readOnly={readOnly} />
            <Input placeholder="Other Degrees" value={r.otherDegree ?? ""} onChange={(e) => set(r.slot, "otherDegree", e.target.value)} readOnly={readOnly} />
            <Input placeholder="Title *" value={r.title ?? ""} onChange={(e) => set(r.slot, "title", e.target.value)} readOnly={readOnly} />
            <Input placeholder="Other titles" value={r.otherTitles ?? ""} onChange={(e) => set(r.slot, "otherTitles", e.target.value)} readOnly={readOnly} />
            <Input className="col-span-2" placeholder="Because of your expertise in… *" value={r.subdiscipline ?? ""} onChange={(e) => set(r.slot, "subdiscipline", e.target.value)} readOnly={readOnly} />
            <Input className="col-span-2" placeholder="Best-match paper title *" value={r.bestMatchPaperTitle ?? ""} onChange={(e) => set(r.slot, "bestMatchPaperTitle", e.target.value)} readOnly={readOnly} />
            <Input className="col-span-2" type="url" placeholder="Best-match paper URL or DOI *" value={r.bestMatchUrl ?? ""} onChange={(e) => set(r.slot, "bestMatchUrl", e.target.value)} readOnly={readOnly} />
            <Input placeholder="Institutional profile URL *" value={r.profileUrl ?? ""} onChange={(e) => set(r.slot, "profileUrl", e.target.value)} readOnly={readOnly} />
            <Input placeholder="PubMed / publications URL *" value={r.pubmedUrl ?? ""} onChange={(e) => set(r.slot, "pubmedUrl", e.target.value)} readOnly={readOnly} />
            {(r.subdiscipline?.trim() || r.bestMatchPaperTitle?.trim()) && (
              <p className="col-span-2 rounded-md border border-blue-100 bg-blue-50 px-2 py-1.5 text-xs italic text-blue-950">
                Justification preview: Because of your expertise in {r.subdiscipline?.trim() || "…"}, and especially your
                paper "{r.bestMatchPaperTitle?.trim() || "…"}", we think you'd be a terrific reviewer for this preprint.
              </p>
            )}
          </div>
        </div>
      ))}
      {!readOnly && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-500">Name + email auto-propose; add profile fields, sub-discipline, and best-match to land it proposal-ready.</p>
          <div className="flex flex-col items-end gap-1">
            <Button onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? "Saving…" : "Save model reviewers"}
            </Button>
            {save.isError && (
              <p className="text-xs text-red-700">
                {save.error instanceof ApiError ? save.error.message : "Could not save model reviewers."}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Suggested phrases for expertise (deck edit; editorial stage) ─────────────
// ~3 options stored newline-separated under AE_SUBDISCIPLINES_KEY; do-not-present. Slot values
// round-trip raw (join "\n") so mid-typing empties survive; consumers parse via aeSubdisciplines.
const WORD_BOX_SLOTS = [0, 1, 2];

function AeSubdisciplineWordBox({
  value,
  onChange,
  readOnly,
}: {
  value: string;
  onChange: (v: string) => void;
  readOnly: boolean;
}): JSX.Element {
  const slots = value.split("\n");
  const slotVal = (i: number): string => slots[i] ?? "";
  const setSlot = (i: number, v: string): void => {
    const next = WORD_BOX_SLOTS.map((j) => (j === i ? v : slotVal(j)));
    onChange(next.join("\n"));
  };
  return (
    <div className="space-y-1.5 rounded-md border border-slate-100 bg-slate-50/60 p-2">
      <h3 className="text-xs font-semibold text-slate-600">
        Expertise phrases for invite email{" "}
        <Badge tone="slate">not shown at pitch</Badge>
      </h3>
      <p className="text-xs text-slate-500">
        Write up to 3 short expertise areas for this preprint (e.g. &quot;vaccine epidemiology&quot;).
        Students pick one when proposing a reviewer — it fills in: &quot;Because of your expertise
        in …&quot;.
      </p>
      <div className="grid gap-1.5 sm:grid-cols-3">
        {WORD_BOX_SLOTS.map((i) => (
          <Input
            key={i}
            placeholder={i === 0 ? "e.g. vaccine epidemiology" : `Expertise area ${i + 1}`}
            value={slotVal(i)}
            onChange={(e) => setSlot(i, e.target.value)}
            readOnly={readOnly}
          />
        ))}
      </div>
    </div>
  );
}

// ── FR-P11: Presentation view — split panel (Presented vs muted Context) ─────
// Claim star ratings live in the Context ("not presented") column, each in the SAME grid
// row as its research question / main claim so they stay vertically aligned. Region membership
// for every field lives in ./pitch-regions.ts, which has the tests.

/** Angel (Stef mtg 8/20): the abstract leads the pitch view — muted styling as before, truncated
 *  with a See more / See less toggle instead of the full wall of text in the context rail. */
const ABSTRACT_PREVIEW_CHARS = 320;

function AbstractDisclosure({ text }: { text: string }): JSX.Element | null {
  const [expanded, setExpanded] = useState(false);
  const body = text.trim();
  if (!body) return null;
  const truncatable = body.length > ABSTRACT_PREVIEW_CHARS;
  return (
    <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-slate-500">
      <span className="text-xs font-semibold uppercase tracking-wide">
        Abstract <span className="text-[10px] font-medium uppercase text-slate-400">not presented</span>
      </span>
      {expanded || !truncatable ? (
        <RichText className="text-slate-500">{body}</RichText>
      ) : (
        <p className="text-sm leading-relaxed text-slate-500">{body.slice(0, ABSTRACT_PREVIEW_CHARS)}…</p>
      )}
      {truncatable && (
        <button
          type="button"
          className="mt-1 text-xs font-medium text-brand hover:underline"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "See less" : "See more"}
        </button>
      )}
    </div>
  );
}

// WO-7/22 Track B [15:12–17:46]: presentation view delineates OWNERSHIP in three regions — the
// STUDENT box (claims half), the AE box (relabeled reviewer-profile prompts + the star ratings),
// and the do-not-present Context box — so it is explicit who says what at the pitch.
// Until To Pitch(Editorial), the AE box is hidden: the student pitches everything at the team
// meeting (criteria + profile fold into the student column). Editorial meetings use the AE split.
function PresentationSplit({ s, changeStatus }: { s: SelectedView; changeStatus: JSX.Element }): JSX.Element {
  const editorialStage = EDITORIAL_STAGES.includes(s.status);
  const mprStage = MPR_STAGES.includes(s.status);
  const pitch = s.pitch as Record<string, unknown>;
  const val = (key: string): string => String(pitch[key] ?? "");
  const num = (key: string): number => Number.parseInt(val(key), 10) || 0;
  // Sync stores Abstract as selected/preprint.abstract, not in pitch (DECK_KEYS has no Abstract).
  const abstract = val("Abstract") || s.abstract || s.abstractBlurb || "";
  const aeSubs = aeSubdisciplines(pitch);
  const claims = CLAIM_PAIRS.filter((c) => val(c.rq.key).trim() || val(c.claim.key).trim());
  // Limitations render in their own aligned row beside Overall Strength (Angel, Stef mtg 8/20).
  const tail = PRESENTED_TAIL.filter((f) => f.key !== "Potential Limitation" && val(f.key).trim());
  const limitations = val("Potential Limitation").trim();
  const overallStrength = num("Strength of Evidence");
  const profilePresented = PRESENTED_PROFILE_FIELDS.filter((f) => val(f.key).trim());
  return (
    <div className="space-y-4">
      {/* Angel (Stef mtg 8/20): abstract at the top, truncated with See more. */}
      <AbstractDisclosure text={abstract} />

      {/* Column headers — Student (presented) vs Context (not presented) */}
      <div className="grid gap-4 lg:grid-cols-[1.35fr_1fr]">
        <div className="rounded-lg border border-blue-200 bg-blue-50/50 px-4 py-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-blue-800">
            🧑‍🎓 Student presents
          </h2>
          <p className="text-xs text-slate-500">
            {editorialStage
              ? "Research questions, claims, and limitations."
              : "Team meeting — the student pitches everything (claims, criteria, and reviewer-profile prompts)."}
          </p>
        </div>
        <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-3">
          <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-400">
            Context — do not present <Badge tone="slate">muted</Badge>
          </h2>
          <p className="text-xs text-slate-400">Claim ratings sit beside their claim; not read aloud.</p>
        </div>
      </div>

      {/* One row per claim: presented text | that claim's rating (aligned). */}
      {claims.map((c) => {
        const rating = num(c.rating);
        const confidence = num(c.confidence);
        return (
          <div key={c.rq.key} className="grid items-stretch gap-4 lg:grid-cols-[1.35fr_1fr]">
            <div className="space-y-2 rounded-lg border border-blue-100 bg-white p-3 shadow-sm">
              {val(c.rq.key).trim() && (
                <div className="space-y-1">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{c.rq.label}</span>
                  <RichText>{val(c.rq.key)}</RichText>
                </div>
              )}
              {val(c.claim.key).trim() && (
                <div className="space-y-1">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{c.claim.label}</span>
                  <RichText>{val(c.claim.key)}</RichText>
                </div>
              )}
            </div>
            <div className="flex flex-col justify-center gap-2 rounded-lg border border-dashed border-slate-200 bg-slate-50 p-3 text-slate-500">
              <span className="text-xs font-semibold uppercase tracking-wide">
                {c.ratingLabel}{" "}
                <span className="text-[10px] font-medium uppercase text-slate-400">not presented</span>
              </span>
              {rating > 0 || confidence > 0 ? (
                <div className="flex flex-col gap-2 text-xs">
                  {rating > 0 && (
                    <span className="flex flex-wrap items-center gap-1.5">
                      Rating <StarRating value={rating} readOnly label={`${c.claim.label} rating`} />
                    </span>
                  )}
                  {confidence > 0 && (
                    <span className="flex flex-wrap items-center gap-1.5">
                      Confidence <StarRating value={confidence} readOnly label={`${c.claim.label} confidence`} />
                    </span>
                  )}
                </div>
              ) : (
                <p className="text-xs italic text-slate-400">No rating on file.</p>
              )}
            </div>
          </div>
        );
      })}

      {/* Angel (Stef mtg 8/20): Potential Limitations (presented) lines up side-by-side with the
          Overall Strength stars (muted), exactly like each claim lines up with its rating. */}
      {(limitations || overallStrength > 0) && (
        <div className="grid items-stretch gap-4 lg:grid-cols-[1.35fr_1fr]">
          <div className="space-y-1 rounded-lg border border-blue-100 bg-white p-3 shadow-sm">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Potential Limitations
            </span>
            {limitations ? (
              <RichText>{limitations}</RichText>
            ) : (
              <p className="text-xs italic text-slate-400">None noted.</p>
            )}
          </div>
          <div className="flex flex-col justify-center gap-2 rounded-lg border border-dashed border-slate-200 bg-slate-50 p-3 text-slate-500">
            <span className="text-xs font-semibold uppercase tracking-wide">
              Overall Strength{" "}
              <span className="text-[10px] font-medium uppercase text-slate-400">not presented</span>
            </span>
            {overallStrength > 0 ? (
              <StarRating value={overallStrength} readOnly label="Overall Strength" />
            ) : (
              <p className="text-xs italic text-slate-400">No rating on file.</p>
            )}
          </div>
        </div>
      )}

      {/* Student tail (+ AE content at team meeting) on the left; Context on the right */}
      <div className="grid gap-4 lg:grid-cols-[1.35fr_1fr]">
        <div className="space-y-4">
          {tail.length > 0 && (
            <Card className="border-blue-200">
              <CardBody className="space-y-5">
                {tail.map((f) => (
                  <div key={f.key} className="space-y-1">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{f.label}</span>
                    <RichText>{val(f.key)}</RichText>
                  </div>
                ))}
              </CardBody>
            </Card>
          )}

          {/* Pre-editorial: student pitches criteria + profile (no separate AE column). */}
          {!editorialStage && (
            <Card className="border-blue-200">
              <CardBody className="space-y-5">
                <PitchCriteria pitch={pitch} />
                {profilePresented.map((f) => (
                  <div key={f.key} className="space-y-1">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{f.label}</span>
                    <RichText>{val(f.key)}</RichText>
                  </div>
                ))}
              </CardBody>
            </Card>
          )}

          {/* Editorial+: AE presents criteria + profile prompts. */}
          {editorialStage && (
            <Card className="border-violet-200">
              <CardHeader className="bg-violet-50/50">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-violet-800">
                  🧭 AE presents
                </h2>
                <p className="text-xs text-slate-500">
                  The four ratings (with a one-line note each) and the reviewer-profile prompts.
                </p>
              </CardHeader>
              <CardBody className="space-y-5">
                <PitchCriteria pitch={pitch} />
                {profilePresented.map((f) => (
                  <div key={f.key} className="space-y-1">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{f.label}</span>
                    <RichText>{val(f.key)}</RichText>
                  </div>
                ))}
              </CardBody>
            </Card>
          )}
        </div>

        <Card className="h-fit border-dashed bg-slate-50">
          <CardHeader className="border-dashed">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">More context</h2>
            <p className="text-xs text-slate-400">Reference only — not read aloud.</p>
          </CardHeader>
          <CardBody className="space-y-4 text-slate-500">
            {/* Abstract moved to the top of the pitch view (Angel, Stef mtg 8/20). */}
            {editorialStage && aeSubs.length > 0 && (
              <div className="space-y-1">
                <span className="text-xs font-semibold uppercase tracking-wide">
                  Expertise phrases for invite email{" "}
                  <span className="text-[10px] font-medium uppercase text-slate-400">not shown at pitch</span>
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {aeSubs.map((sub) => (
                    <Badge key={sub} tone="slate">{sub}</Badge>
                  ))}
                </div>
              </div>
            )}
            {/* Abstract leads the page; Strength of Evidence renders beside Limitations above. */}
            {CONTEXT_TEXT.filter(
              (f) => f.key !== "Abstract" && f.key !== "Strength of Evidence" && val(f.key).trim(),
            ).map((f) => (
              <div key={f.key} className="space-y-1">
                <span className="text-xs font-semibold uppercase tracking-wide">
                  {f.label} <span className="text-[10px] font-medium uppercase text-slate-400">not presented</span>
                </span>
                {f.stars ? (
                  <StarRating value={Number.parseInt(val(f.key), 10) || 0} readOnly label={f.label} />
                ) : (
                  <RichText className="text-slate-500">{val(f.key)}</RichText>
                )}
              </div>
            ))}

            {/* Similar articles + key reviewer question render via CONTEXT_TEXT (after Method 3). */}

            {/* Model peer reviewers: editorial stage only; each card carries its own heading. */}
            {mprStage && s.modelReviewers.length > 0 && (
              <ModelReviewers reviewers={s.modelReviewers} hideHeading />
            )}
          </CardBody>
        </Card>
      </div>

      {/* Full-width gate below the presentation split. */}
      {changeStatus}
    </div>
  );
}

/** R-011 + WO-7/22 Track B [13:05–13:57]: the four criteria — stars each with a SHORT brief note
 *  (one line, not a paragraph; Steph's ask), presented at all meetings alongside the stars. */
/**
 * WO-7/30 Track B [17:23–18:59]: what 2★ vs 5★ means, so scoring is uniform across raters.
 * The one-line hint always shows; the link out only once a guide URL is configured
 * (RATINGS_GUIDE_URL) — the guide content itself is Steph's/editorial's to write.
 */
function RatingsGuideHint(): JSX.Element {
  const { features } = useFeatures();
  const href = features.ratingsGuideUrl?.trim();
  return (
    <span className="text-[11px] font-normal normal-case text-slate-400">
      1★ = weak · 3★ = solid · 5★ = exceptional
      {href && (
        <>
          {" · "}
          <a href={href} target="_blank" rel="noopener" className="font-medium text-brand hover:underline">
            Rating guide ↗
          </a>
        </>
      )}
    </span>
  );
}

function PitchCriteria({ pitch }: { pitch: Record<string, unknown> }): JSX.Element {
  const val = (k: string): string => String(pitch[k] ?? "");
  const rows = PITCH_CRITERIA.map((c) => ({
    label: c.label,
    stars: Number.parseInt(val(c.key), 10) || 0,
    note: val(criteriaNoteKey(c.key)).trim(),
  }));
  return (
    <div className="space-y-3">
      <span className="flex flex-wrap items-baseline gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Why this preprint — four criteria
        </span>
        <RatingsGuideHint />
      </span>
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.label} className="flex flex-wrap items-center gap-3">
            <span className="w-24 shrink-0 text-xs font-semibold uppercase tracking-wide text-slate-500">{r.label}</span>
            <StarRating value={r.stars} readOnly label={r.label} />
            {/* One-line brief note beside the stars — presented with them. */}
            {r.note ? (
              <span className="min-w-0 flex-1 truncate text-xs italic text-slate-600" title={r.note}>
                {r.note}
              </span>
            ) : (
              <span className="text-xs italic text-slate-400">—</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Change-Status gate — advance / select / reject, gated by can(...) and the same action mapping
// the API enforces. Selection is direct + AE-gated (July-2 FR-J4).
//
// July-8 FR-N2/FR-N3 turn the two AE gates into a visible, auditable checklist (the `ae-pitch-deck`
// mockup's "gate" bar): Refine Concepts must have run, and the AE must tick "I have reviewed &
// finalized the deck" — only then does Advance light up. The server enforces both (409 otherwise);
// disabling the button is a courtesy, not the control. Setting `Selected` still fires the "changes
// discussed in editorial?" confirm (FR-J5 semantics, retained by FR-N2).
const NEXT: Partial<Record<SelectedStatus, { status: SelectedStatus; label: string; action: string }>> = {
  Taken: { status: "To Pitch(Team)", label: "Ready to pitch (Team)", action: "setToPitchTeam" },
  "To Pitch(Team)": { status: "To Pitch(Editorial)", label: "Advance to Editorial", action: "advanceToPitchEditorial" },
  "To Pitch(Editorial)": { status: "Selected", label: "Set Selected", action: "setToSelected" },
};
const REJECT_TARGET: Partial<Record<SelectedStatus, SelectedStatus>> = {
  Unread: "Rejected Once",
  "To Pitch(Team)": "Team Rejected",
  "To Pitch(Editorial)": "Editorial Rejected",
};
/** The AE gates the FR-N2/N3 checklist guards. The student's own → To Pitch(Team) is not gated. */
const AE_GATES: SelectedStatus[] = ["To Pitch(Team)", "To Pitch(Editorial)"];

const fmtStamp = (iso: string): string => new Date(iso).toLocaleString();

function ChangeStatus({
  s,
  own,
  canProfile,
  beforeAdvance,
  onAdvancedToEditorial,
  listPath,
}: {
  s: SelectedView;
  own: boolean;
  canProfile: boolean;
  /** Flushes unsaved deck edits before advancing — Advance is save+advance (Angel, 8/20). */
  beforeAdvance?: () => Promise<void>;
  /** After → To Pitch(Editorial), open edit mode so AE can fill suggested phrases + MPRs. */
  onAdvancedToEditorial?: () => void;
  /** Angel Q4: unclaim returns to the filtered list, not Unclaimed. */
  listPath: string;
}): JSX.Element | null {
  const qc = useQueryClient();
  const can = useCan();
  const navigate = useNavigate();
  const ctx = { domain: s.domain, beforePitching: s.status === "Taken" };
  const next = NEXT[s.status];
  const rejectTarget = REJECT_TARGET[s.status];
  const [reason, setReason] = useState("");
  const canUnclaim = s.status === "Taken" && (own || can("advanceToPitchEditorial", {}));

  const invalidate = (): void => {
    qc.invalidateQueries({ queryKey: ["selected"] });
    qc.invalidateQueries({ queryKey: qk.selectedOne(s.id) });
  };
  const advance = useMutation({
    mutationFn: (status: SelectedStatus) =>
      api.post(`/preprints/selected/${s.id}/advance`, { status, ...(reason ? { reason } : {}) }),
    onSuccess: (_data, status) => {
      invalidate();
      if (status === "To Pitch(Editorial)") onAdvancedToEditorial?.();
    },
  });
  const refine = useMutation({
    mutationFn: () => api.post(`/preprints/selected/${s.id}/refine`, { mode: "reviewers" }),
    onSuccess: invalidate,
  });
  const finalize = useMutation({
    mutationFn: (finalized: boolean) => api.post(`/preprints/selected/${s.id}/finalize`, { finalized }),
    onSuccess: invalidate,
  });
  const unclaim = useMutation({
    mutationFn: () => api.post(`/preprints/selected/${s.id}/unclaim`),
    onSuccess: () => {
      invalidate();
      navigate(listPath);
    },
  });
  // July-8 FR-L8: AE-gated advances + gate rejects are cross-domain (no domain in the check).
  const canAdvance = !!next && (next.action === "setToPitchTeam" ? own || can("setToPitchTeam", ctx) : can(next.action as never, {}));
  const canReject =
    !!rejectTarget &&
    (s.status === "Unread"
      ? can("rejectFromPool", {})
      : can((next?.action ?? "advanceToPitchEditorial") as never, {}));
  if (!next && !rejectTarget && !canUnclaim) return null;

  // ── The FR-N2/N3 gate state ──
  const atAeGate = AE_GATES.includes(s.status);
  const refining = s.proposalStatus === "Created";
  const refined = !!s.refinedAt;
  const finalized = !!s.finalizedAt;
  // FR-N3 blocks both AE gates; FR-N2's Finalize is specifically the door into Editorial.
  const needsFinalize = s.status === "To Pitch(Team)";
  // WO-7/30 Track C [13:00]: "this section here, paper-level COIs should disappear". The question
  // is gone from the gate, and with it the advance blocker — leaving the blocker without its
  // control would have made the editorial advance unreachable. Retires SoftR parity R-017; the
  // stored `Paper COI` key and its save path are untouched, so answers survive and restoring the
  // gate is a small change. See docs/qa/2026-07-30-global-ae-demo-wo.md.
  const blockers = atAeGate
    ? [!refined && "Run Refine Concepts first", needsFinalize && !finalized && "Finalize the deck first"].filter(
        Boolean,
      )
    : [];
  const gateBlocked = blockers.length > 0;

  return (
    <Card>
      <CardHeader>
        <h2 className="text-sm font-semibold">Change Status</h2>
        <p className="text-xs text-slate-500">
          {atAeGate
            ? "Refine and finalize before this deck can advance — both are recorded against your name."
            : "Record your gate decision."}
        </p>
      </CardHeader>

      {/* FR-K8: Refine sits on BOTH pitch gates ("sometimes I get this wrong, so I have to
          re-refine"). FR-N3: it is now a required gate, not just a button. */}
      {atAeGate && canProfile && (
        <CardBody className="flex flex-wrap items-center gap-3 border-b border-slate-100">
          <Button variant="secondary" onClick={() => refine.mutate()} disabled={refine.isPending || refining}>
            {refining ? "Refining…" : "↻ Refine Concepts / Updated Reviewers"}
          </Button>
          {refined ? (
            <Badge tone="green">✓ Refined {fmtStamp(s.refinedAt!)}</Badge>
          ) : (
            <Badge tone="amber">Required before advancing</Badge>
          )}
        </CardBody>
      )}

      {/* WO-7/30 Track C [13:00]: the paper-level COI question was removed from this gate. */}

      {/* FR-N2: the first-class Finalize control that replaces the pop-up-only gate. */}
      {atAeGate && canProfile && (
        <CardBody className="flex flex-wrap items-center gap-3 border-b border-slate-100">
          <label className="flex items-center gap-2 text-sm font-medium">
            <input
              type="checkbox"
              checked={finalized}
              disabled={finalize.isPending}
              onChange={(e) => finalize.mutate(e.target.checked)}
            />
            I have reviewed &amp; finalized the deck
          </label>
          {finalized ? (
            <Badge tone="green">
              Finalized by {s.finalizedBy} · {fmtStamp(s.finalizedAt!)}
            </Badge>
          ) : (
            <Badge tone="amber">Required before Editorial</Badge>
          )}
        </CardBody>
      )}

      <CardBody className="flex flex-wrap items-center gap-2">
        {canAdvance && next && (
          <>
            <Button
              onClick={async () => {
                // FR-N2 keeps the "changes discussed in editorial?" confirm on → Selected.
                if (!confirmAdvance(next.status)) return;
                // Save any dirty deck fields first; a failed save blocks the advance.
                try {
                  await beforeAdvance?.();
                } catch {
                  return;
                }
                advance.mutate(next.status);
              }}
              disabled={advance.isPending || gateBlocked}
              title={gateBlocked ? blockers.join(" · ") : undefined}
            >
              {next.label}
            </Button>
            {canUnclaim && (
              <Button
                variant="secondary"
                onClick={() => {
                  if (!window.confirm("Return this preprint to Unclaimed?")) return;
                  unclaim.mutate();
                }}
                disabled={unclaim.isPending}
              >
                {unclaim.isPending ? "Unclaiming…" : "Unclaim"}
              </Button>
            )}
            {gateBlocked && <p className="text-xs text-amber-700">{blockers.join(" · ")}.</p>}
          </>
        )}
        {/* R-016: the Reject control leads; the optional reason follows it (SoftR order). */}
        {canReject && rejectTarget && (
          <>
            <Button
              variant="danger"
              onClick={() => {
                if (
                  window.confirm(
                    s.status === "Unread"
                      ? "Reject this unclaimed preprint? It leaves the Unclaimed pool (Rejected Once)."
                      : `Reject this preprint at the ${s.status} gate?`,
                  )
                ) {
                  advance.mutate(rejectTarget);
                }
              }}
              disabled={advance.isPending}
            >
              Reject
            </Button>
            <Input className="max-w-xs" placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} />
          </>
        )}
        {!canAdvance && !canReject && !canUnclaim && (
          <p className="text-xs text-slate-500">No status changes available to you here.</p>
        )}
      </CardBody>
    </Card>
  );
}
